'use client'

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import { isEmptyString, isNilOrEmptyString } from '@/shared/lib'

import { ACCOUNT_TOAST_CONTAINER_ID } from './toast'
import { ERROR, useVerifyAccount } from '../api/verify-account'

type AccountVerificationState = {
  isAccountVerified: boolean
  isVerificationFailed: boolean
  action: ReturnType<typeof useVerifyAccount>['action']
  verifyAccount: () => Promise<void>
  markAsUnverified: () => void
  accountTrigger: () => Promise<boolean>
}

const AccountVerificationContext =
  createContext<AccountVerificationState | null>(null)

export const AccountVerificationProvider = ({
  children,
}: PropsWithChildren) => {
  const { getValues, clearErrors, trigger, setError } = useFormContext()

  const {
    action,
    isVerified,
    isFailed,
    markAsUnverified,
    handleAccountVerificationFailed,
  } = useVerifyAccount()

  const validateRequiredFields = useCallback(async () => {
    clearErrors([
      'bankName',
      'bankAccount',
      'accountHolder',
      'root.accountError',
      'root.accountServerError',
    ])

    const result = await trigger(['bankName', 'bankAccount'], {
      shouldFocus: true,
    })

    return result
  }, [clearErrors, trigger])

  const accountTrigger = useCallback(async () => {
    const isValid = await validateRequiredFields()

    if (!isValid) {
      return false
    }

    const accountHolder = getValues('accountHolder')

    if (isFailed && isEmptyString(accountHolder)) {
      handleAccountVerificationFailed()

      return false
    }

    if (isFailed && !isEmptyString(accountHolder)) {
      return true
    }

    const isAccountHolderEmpty = isNilOrEmptyString(accountHolder)

    if (!isVerified && isAccountHolderEmpty) {
      setError('root.accountError', {
        type: ERROR.NOT_CHECKED,
        message: '예금주 확인 버튼을 클릭해 주세요.',
      })
      setError(
        'accountHolder',
        {
          type: ERROR.NOT_CHECKED,
        },
        {
          shouldFocus: true,
        },
      )
      toast(`예금주 확인 버튼을 클릭해 주세요.`, {
        containerId: ACCOUNT_TOAST_CONTAINER_ID,
      })

      return false
    }

    return true
  }, [
    getValues,
    handleAccountVerificationFailed,
    isFailed,
    isVerified,
    setError,
    validateRequiredFields,
  ])

  const verifyAccount = useCallback(async () => {
    const canExecute = await validateRequiredFields()

    if (!canExecute) {
      return
    }

    action.execute({
      bankName: getValues('bankName'),
      accountNumber: getValues('bankAccount'),
    })
  }, [action, getValues, validateRequiredFields])

  const values = useMemo(
    () => ({
      isAccountVerified: isVerified,
      isVerificationFailed: isFailed,
      action,
      verifyAccount,
      markAsUnverified,
      accountTrigger,
    }),
    [
      isVerified,
      isFailed,
      action,
      verifyAccount,
      markAsUnverified,
      accountTrigger,
    ],
  )

  return (
    <AccountVerificationContext.Provider value={values}>
      {children}
    </AccountVerificationContext.Provider>
  )
}

export const useAccountVerification = () => {
  const context = useContext(AccountVerificationContext)

  if (!context) {
    throw new Error(
      'useAccountVerification must be used within a AccountVerificationProvider',
    )
  }

  return context
}
